import React, { useState, useRef, useEffect } from 'react';
import { User, Users, ChevronDown, ArrowLeft } from 'lucide-react';
import { useGoalContext } from '../hooks/useGoalContext';

interface GoalContextSwitcherProps {
  userId: string | undefined;
}

/**
 * Component for switching between personal environment and team goal contexts
 * Similar to EntitySwitcher but for goal context
 */
export function GoalContextSwitcher({ userId }: GoalContextSwitcherProps) {
  const {
    activeGoalId,
    availableGoals,
    isInTeamContext,
    activeGoal,
    setActiveGoalId,
    loading,
  } = useGoalContext(userId);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleGoalSelect = (goalId: string) => {
    setActiveGoalId(goalId);
    setIsOpen(false);
  };

  const handleBackToPersonal = () => {
    setActiveGoalId(null);
    setIsOpen(false);
  };

  // Don't render if no team goals available
  if (!loading && availableGoals.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          transition-all duration-200
          ${
            isInTeamContext
              ? 'bg-orange-50 border-2 border-orange-200 text-orange-700'
              : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:bg-gray-100'
          }
        `}
      >
        {isInTeamContext ? (
          <>
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Team: {activeGoal?.goalTitle}</span>
          </>
        ) : (
          <>
            <User className="w-4 h-4" />
            <span className="text-sm font-medium">Eigen omgeving</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Context schakelen</h3>
          </div>

          {/* Personal Environment */}
          <button
            onClick={handleBackToPersonal}
            className={`
              w-full flex items-center gap-3 px-4 py-3 text-left
              transition-colors duration-150
              ${
                !isInTeamContext
                  ? 'bg-blue-50 border-l-4 border-blue-500'
                  : 'hover:bg-gray-50 border-l-4 border-transparent'
              }
            `}
          >
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center
              ${isInTeamContext ? 'bg-gray-100' : 'bg-blue-100'}
            `}>
              <User className={`w-4 h-4 ${isInTeamContext ? 'text-gray-600' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1">
              <div className={`text-sm font-medium ${isInTeamContext ? 'text-gray-700' : 'text-blue-700'}`}>
                Eigen omgeving
              </div>
              <div className="text-xs text-gray-500">Persoonlijke doelen en taken</div>
            </div>
            {!isInTeamContext && (
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
            )}
          </button>

          {/* Divider */}
          {availableGoals.length > 0 && (
            <div className="px-4 py-2 bg-gray-50">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Team Doelen ({availableGoals.length})
              </div>
            </div>
          )}

          {/* Team Goals */}
          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              Laden...
            </div>
          ) : availableGoals.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              Je bent nog geen teamlid van een doel
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {availableGoals.map((goal) => {
                const isActive = activeGoalId === goal.goalId;
                return (
                  <button
                    key={goal.goalId}
                    onClick={() => handleGoalSelect(goal.goalId)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 text-left
                      transition-colors duration-150
                      ${
                        isActive
                          ? 'bg-orange-50 border-l-4 border-orange-500'
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${isActive ? 'bg-orange-100' : 'bg-gray-100'}
                    `}>
                      <Users className={`w-4 h-4 ${isActive ? 'text-orange-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isActive ? 'text-orange-700' : 'text-gray-700'}`}>
                        {goal.goalTitle}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="capitalize">{getRoleLabel(goal.role)}</span>
                        {goal.function && (
                          <>
                            <span>•</span>
                            <span>{goal.function}</span>
                          </>
                        )}
                        {goal.accessLevel === 'agenda-only' && (
                          <>
                            <span>•</span>
                            <span className="text-amber-600">Beperkte toegang</span>
                          </>
                        )}
                      </div>
                    </div>
                    {isActive && (
                      <div className="w-2 h-2 bg-orange-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Back Button (when in team context) */}
          {isInTeamContext && (
            <>
              <div className="border-t border-gray-200" />
              <button
                onClick={handleBackToPersonal}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Terug naar eigen omgeving</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    owner: 'eigenaar',
    member: 'teamlid',
    va: 'VA',
    manager: 'manager',
    assistant: 'assistent',
  };
  return labels[role] || role;
}
